import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import type { BimPriceLevelEnum } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { cnMixSpace, Loader } from 'uikit';

import type { ColDef, ColGroupDef, GridOptions, IDatasource, RowClassRules } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import React, { useEffect, useMemo, useState } from 'react';

import { BimModalTitleWithPriceLevel } from '../BimModalTitleWithPriceLevel';

import Styles from './BimWorkOrderChange.module.css';

type TDetailsData<TData> = {
    data?: TData[];
    datasource?: IDatasource;
    columns: (ColDef<TData> | ColGroupDef<TData>)[];
    modalTitle: string;
    priceLevel: BimPriceLevelEnum | undefined;
    onClose: () => void;
    baseRow?: AnyObject[];
    isDetailsLoading?: boolean;
    defaultColDef?: ColDef<TData>;
    rowClassRules?: RowClassRules<TData>;
    styleClass?: string;
    suppressDragLeaveHidesColumns?: boolean;
    gridOptions?: GridOptions<TData>;
};

export const BimWorkOrderPriceRowDetails = <TData = any,>({
    modalTitle,
    data,
    datasource,
    baseRow = [],
    columns,
    onClose,
    isDetailsLoading,
    defaultColDef,
    rowClassRules,
    styleClass,
    suppressDragLeaveHidesColumns = true,
    priceLevel,
}: TDetailsData<TData>) => {
    const [isFlickerPreventionStyleActive, setIsFlickerPreventionStyleActive] = useState(() => !!datasource);

    const commonOptions = useMemo<AgGridReactProps<TData>>(
        () => ({
            animateRows: true,
            rowSelection: 'multiple',
            suppressRowClickSelection: true,
            overlayNoRowsTemplate: 'Нет данных',
            defaultColDef,
            rowClassRules,
            icons: AG_GRID_ICON_SETUP,
            suppressDragLeaveHidesColumns,
            tooltipShowDelay: 200,
        }),
        [defaultColDef, rowClassRules, suppressDragLeaveHidesColumns],
    );

    useEffect(() => {
        if (isFlickerPreventionStyleActive) {
            setTimeout(() => {
                setIsFlickerPreventionStyleActive(false);
            }, 0);
        }
    }, [isFlickerPreventionStyleActive]);

    return (
        <ModalDialog
            isOpen
            title={<BimModalTitleWithPriceLevel priceLevel={priceLevel} modalTitle={modalTitle} />}
            onClose={onClose}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            isLoading={isDetailsLoading}
            withBottomButtons={false}
            style={{ visibility: isFlickerPreventionStyleActive ? 'hidden' : 'visible' }}
        >
            <div
                className={`ag-theme-alpine ${Styles.turnOffZebra} ${styleClass} ${cnMixSpace({
                    pV: 's',
                    pH: 'l',
                })}`}
            >
                {datasource && (
                    <>
                        <AgGridTable
                            {...commonOptions}
                            datasource={datasource}
                            columnDefs={columns}
                            pinnedTopRowData={baseRow}
                            rowModelType="infinite"
                            maxConcurrentDatasourceRequests={1}
                            maxBlocksInCache={50}
                            cacheBlockSize={50}
                        />
                        {isFlickerPreventionStyleActive && <Loader className={Styles.flickerPreventionLoader} />}
                    </>
                )}

                {!datasource && (
                    <AgGridTable {...commonOptions} rowData={data} columnDefs={columns} pinnedTopRowData={baseRow} />
                )}
            </div>
        </ModalDialog>
    );
};

BimWorkOrderPriceRowDetails.displayName = 'BimWorkOrderPriceRowDetails';
